import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, UserPlus, Building2, Clock, Wallet, ArrowUpRight, ArrowRight, Briefcase } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend } from 'recharts';
import { employeeApi, salaryMatrixApi, masterApi, requirementTatApi, associateApi, ALL_UNITS } from '../lib/api';
import { topDepartment } from '../lib/utils';
import { useUnit } from '../contexts/UnitContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const ACCENTS = ['#6B8CB8', '#6BAE97', '#9389C2', '#DD9A93', '#D9B26B', '#8FB4D4', '#5A6B96', '#7FBBA6', '#B09A6F'];
const toTime = (doj) => { if (!doj) return 0; const s = String(doj); if (s.includes('.')) { const [d, m, y] = s.split('.'); return new Date(`${y}-${m}-${d}`).getTime() || 0; } return new Date(s).getTime() || 0; };
const joinYear = (e) => e.year_join || (e.doj ? new Date(toTime(e.doj)).getFullYear() : null);
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

/** Counts a number up on mount. One rAF chain, cancelled on unmount, and it
 *  returns the final value immediately when the OS asks for reduced motion,
 *  so it is never an accessibility or performance tax. */
function useCountUp(target, ms = 850) {
  const numeric = typeof target === 'number' && isFinite(target);
  const [n, setN] = useState(numeric ? 0 : target);
  const raf = useRef(0);
  useEffect(() => {
    if (!numeric) { setN(target); return undefined; }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setN(target); return undefined; }
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / ms);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));   // easeOutCubic
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, ms, numeric]);
  return n;
}

export default function Dashboard() {
  const { activeUnit } = useUnit();
  const navigate = useNavigate();
  const [emps, setEmps] = useState([]);
  const [salaryRows, setSalaryRows] = useState(null);  // null=loading; []=no access/empty
  const [lockedYears, setLockedYears] = useState([]);
  const [loading, setLoading] = useState(true);
  // The Departments master, so the counts below group the way every chart
  // groups: by the department a box carries, not by the spelling on a record.
  const [departments, setDepartments] = useState([]);
  // Open seats, read from the same register the Vacancies & TAT screen shows,
  // so the dashboard can never disagree with it about what is open or how long
  // it has waited. Failure is silent and the panel simply hides: a vacancy
  // list nobody can fetch must not take the whole dashboard down with it.
  const [tat, setTat] = useState(null);

  useEffect(() => {
    setLoading(true);
    employeeApi.list({ unit: activeUnit }).then((r) => setEmps(r.data || [])).catch(() => setEmps([])).finally(() => setLoading(false));
    salaryMatrixApi.get({ unit: activeUnit }).then((r) => { setSalaryRows(r.data.employees || []); setLockedYears(r.data.locked_years || []); }).catch(() => { setSalaryRows([]); setLockedYears([]); });
    masterApi.departments().then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
    requirementTatApi.list({ unit: activeUnit }).then((r) => setTat(r.data)).catch(() => setTat(null));
  }, [activeUnit]);

  // Longest-waiting first — the order they need attention in.
  const openVacancies = useMemo(
    () => (tat?.rows || [])
      .filter((r) => r.status === 'Open')
      .sort((a, b) => (b.tat_days || 0) - (a.tat_days || 0)),
    [tat],
  );
  const longestOpen = tat?.summary?.longest_open_days ?? null;

  const goDept = (name) => name && navigate(`/employees?department=${encodeURIComponent(name)}`);
  const goLevel = (lname) => navigate(`/employees?level=${String(lname).replace('L', '')}`);
  const goYear = (y) => navigate(`/employees?joinYear=${y}`);
  const goDesig = (name) => name && navigate(`/employees?designation=${encodeURIComponent(name)}`);
  // The associates register reads the same filter names off the query string.
  const goAssocDept = (name) => name && navigate(`/associates?department=${encodeURIComponent(name)}`);
  const goAssocDesig = (name) => name && navigate(`/associates?designation=${encodeURIComponent(name)}`);

  const stats = useMemo(() => {
    const thisYear = new Date().getFullYear();
    const byDept = {}, byLevel = {}, byYear = {}, byDesig = {};
    emps.forEach((e) => {
      // Counted by the DEPARTMENT, not by the sub-unit or the spelling: the
      // business runs seven, and this used to count the distinct values stored
      // on people — "16 departments" on Unit 1, five of them Quality.
      const dept = topDepartment(e.department || '—', departments) || '—';
      byDept[dept] = (byDept[dept] || 0) + 1;
      byLevel[e.level ? `L${e.level}` : 'L?'] = (byLevel[e.level ? `L${e.level}` : 'L?'] || 0) + 1;
      byDesig[e.designation || '—'] = (byDesig[e.designation || '—'] || 0) + 1;
      const jy = joinYear(e); if (jy) byYear[jy] = (byYear[jy] || 0) + 1;
    });
    const desigData = Object.entries(byDesig).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
    const deptData = Object.entries(byDept).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    const levelData = Object.entries(byLevel).map(([name, value]) => ({ name, value })).sort((a, b) => parseInt(b.name.replace('L', '') || 0) - parseInt(a.name.replace('L', '') || 0));
    const yearData = Object.entries(byYear).map(([name, value]) => ({ name, value })).sort((a, b) => a.name - b.name);
    const newThisYear = emps.filter((e) => joinYear(e) === thisYear).length;
    const tenures = emps.map((e) => thisYear - (joinYear(e) || thisYear));
    const avgTenure = tenures.length ? (tenures.reduce((a, b) => a + b, 0) / tenures.length).toFixed(1) : '0';
    const recent = [...emps].sort((a, b) => toTime(b.doj) - toTime(a.doj)).slice(0, 5);
    const longest = [...emps].filter((e) => e.doj).sort((a, b) => toTime(a.doj) - toTime(b.doj)).slice(0, 5);
    return { total: emps.length, depts: Object.keys(byDept).length, newThisYear, avgTenure, deptData, levelData, yearData, desigData, recent, longest };
  }, [emps, departments]);

  // ---- Associates on the dashboard -----------------------------------------
  // The same three questions as staff, asked of the shop floor: where are
  // they, who supplies them, what do they do. One click flips the charts
  // between the two halves of headcount.
  //
  // Only the rollups are used, so the read asks for a single row — the server
  // computes the summary over the whole filtered set regardless of paging.
  const [headcountView, setHeadcountView] = useState('staff');
  const [assoc, setAssoc] = useState(null);
  useEffect(() => {
    if (headcountView !== 'associates') return;
    associateApi.list({
      status: 'Active', limit: 1,
      unit: activeUnit === ALL_UNITS ? undefined : (activeUnit || undefined),
    }).then((r) => setAssoc(r.data.summary)).catch(() => setAssoc(null));
  }, [headcountView, activeUnit]);

  const assocCharts = useMemo(() => {
    if (!assoc) return null;
    const pick = (list, n) => (list || []).slice(0, n)
      .map((b) => ({ name: b.label, value: b.headcount }));
    return {
      total: assoc.headcount,
      monthlyCost: assoc.monthly_cost,
      deptData: pick(assoc.by_department, 20),
      contractorData: pick(assoc.by_contractor, 20),
      desigData: pick(assoc.by_designation, 8),
    };
  }, [assoc]);

  const showAssoc = headcountView === 'associates';

  const salary = useMemo(() => {
    if (!salaryRows || !salaryRows.length) return null;
    const years = new Set();
    salaryRows.forEach((e) => Object.keys(e.years || {}).forEach((y) => years.add(parseInt(y))));
    const latest = years.size ? Math.max(...years) : null;
    const lockedNums = (lockedYears || []).map(Number).filter((n) => !isNaN(n));
    // A year stays "proposed" (draft appraisal) until its cycle is locked — never headline a proposal.
    const proposed = new Set();
    salaryRows.forEach((e) => Object.entries(e.years || {}).forEach(([y, c]) => { if (c && c.is_proposed) proposed.add(parseInt(y)); }));
    const finalYears = [...years].filter((y) => !proposed.has(y) || lockedNums.includes(y));
    let useYear;
    if (lockedNums.length) useYear = Math.max(...lockedNums);          // a locked year is authoritative
    else if (finalYears.length) useYear = Math.max(...finalYears);     // else newest finalised (non-proposed) year
    else useYear = latest;                                             // nothing final yet — last resort
    const isLocked = lockedNums.includes(useYear);
    const pending = (latest != null && latest > useYear) ? latest : null;  // a newer year waiting to be locked
    const grossFor = (e) => {
      const ys = e.years || {};
      return (useYear != null && ys[useYear]?.gross != null) ? ys[useYear].gross : 0;
    };
    const byDept = {}; let total = 0;
    salaryRows.forEach((e) => { const g = grossFor(e); byDept[e.department || '—'] = (byDept[e.department || '—'] || 0) + g; total += g; });
    const deptSalary = Object.entries(byDept).map(([name, value]) => ({ name, value, pct: total ? (value / total) * 100 : 0 })).sort((a, b) => b.value - a.value);
    return { total, deptSalary, year: useYear, locked: isLocked, pending };
  }, [salaryRows, lockedYears]);

  const Kpi = ({ icon: Icon, label, value, sub, tile, i = 0 }) => {
    const shown = useCountUp(value);
    const long = String(shown).length;
    // The number sits on its OWN line, under the icon and label, rather than
    // beside the icon.
    //
    // Beside it, five tiles sharing a row left about 110px for the value once
    // the icon and gaps were taken out — and ₹17,66,430 needs 113px even at
    // 21px, so a lakh-scale total ran out over the edge of the card.
    // Shrinking the font further was not the answer: ₹99,999 overflowed at
    // its own step too, so the problem was the width, not the size. On its
    // own line the value gets the full card — about 164px on the same screen
    // — and the figures stay big enough to read.
    return (
      <div className="surface surface-hover enter-up p-5 min-w-0" style={{ '--d': `${i * 60}ms` }}>
        <div className="flex items-center gap-3 min-w-0">
          <span className={`kpi-tile ${tile} flex-shrink-0`} aria-hidden="true">
            <Icon className="w-[20px] h-[20px]" strokeWidth={2.1} />
          </span>
          <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#6B7280] truncate min-w-0">{label}</p>
        </div>
        {/* Still steps down for the longest figures, and `truncate` is the
            backstop: a total nobody anticipated must shorten rather than
            run over the edge, with `title` keeping the full number readable. */}
        <p title={String(shown)}
           className={`font-extrabold leading-none tracking-[-0.03em] text-[#0F172A] tabular-nums mt-3.5 truncate
                       ${long > 12 ? 'text-[22px]'
                         : long > 9 ? 'text-[26px]'
                         : long > 6 ? 'text-[30px]' : 'text-[32px]'}`}>{shown}</p>
        <p className="text-[12px] text-[#6B7280] mt-2 truncate" title={typeof sub === 'string' ? sub : undefined}>{sub}</p>
      </div>
    );
  };
  const ChartCard = ({ title, hint, children, i = 0, onReport }) => (
    <div className="surface enter-up p-5 sm:p-6" style={{ '--d': `${i * 60}ms` }}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h3 className="font-display text-[19px] font-bold tracking-[-0.02em] text-[#0F172A]">{title}</h3>
          {hint && <p className="text-[12px] text-[#64748B] mt-0.5">{hint}</p>}
        </div>
        {onReport && (
          <button type="button" onClick={onReport} className="pill-report flex-shrink-0"
                  aria-label={`View full report: ${title}`}>
            View full report
            <ArrowUpRight className="arr w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" />
          </button>
        )}
      </div>
      {children}
    </div>
  );


  return (
    <div data-testid="dashboard-layout" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto px-3 pb-8 pt-2 sm:px-5 lg:px-7 page-enter">
          <div className="max-w-[1560px] mx-auto">

            {/* ---------------- Hero ---------------- */}
            <section className="enter-up relative overflow-hidden rounded-[1.25rem] mb-6
                                px-6 py-8 sm:px-10 sm:py-10 text-white"
                     style={{ background: 'var(--g-hero)' }}>
              <div className="relative flex flex-wrap items-start justify-between gap-6">
                <div className="min-w-0 max-w-[520px]">
                  <span className="inline-flex items-center gap-2 text-[13px] font-semibold
                                   bg-white/[0.15] rounded-full px-3.5 py-2">
                    <Building2 className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                    {activeUnit ? activeUnit : 'AAMPL HR'}
                  </span>
                  <h1 className="text-[42px] sm:text-[54px] leading-[1.04] font-extrabold tracking-[-0.035em] mt-4">
                    Dashboard
                  </h1>
                  <p className="text-[15px] leading-relaxed text-white/85 mt-2">
                    {loading ? 'Loading your people data…'
                      : `${stats.total} active ${stats.total === 1 ? 'employee' : 'employees'} across ${stats.depts} ${stats.depts === 1 ? 'department' : 'departments'} average tenure ${stats.avgTenure} years.`}
                  </p>
                </div>
                <Link to="/employees"
                      className="group inline-flex items-center gap-2 text-[14px] font-semibold rounded-full
                                 px-5 py-3 bg-white text-[#2563EB] flex-shrink-0
                                 shadow-[0_4px_18px_rgba(15,23,42,0.14)]
                                 transition-all duration-200 hover:-translate-y-0.5
                                 hover:shadow-[0_10px_28px_rgba(15,23,42,0.22)]">
                  View employees
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-200
                                           group-hover:translate-x-[3px] group-hover:-translate-y-[3px]"
                                strokeWidth={2.4} aria-hidden="true" />
                </Link>
              </div>
            </section>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 mb-6">
                {[0, 1, 2, 3, 4].map((k) => (
                  <div key={k} className="surface p-5">
                    <div className="skeleton h-3 w-24 mb-4" />
                    <div className="skeleton h-9 w-20 mb-2.5" />
                    <div className="skeleton h-2.5 w-16" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 mb-6">
                  <Kpi i={0} icon={UserPlus} label="Employees" value={stats.total} sub={activeUnit || 'this unit'} tile="tile-primary" />
                  <Kpi i={1} icon={Users} label={`New in ${new Date().getFullYear()}`} value={stats.newThisYear} sub="joined this year" tile="kpi-new" />
                  <Kpi i={2} icon={Building2} label="Departments" value={stats.depts} sub="in this unit" tile="kpi-dept" />
                  <Kpi i={3} icon={Clock} label="Avg tenure" value={`${stats.avgTenure} yr`} sub="across this unit" tile="kpi-tenure" />
                  {salary && <Kpi i={4} icon={Wallet} label="Total salary" value={inr(salary.total)} sub={salary.locked ? `locked (${salary.year})` : (salary.pending ? `${salary.year} · ${salary.pending} pending lock` : `${salary.year}`)} tile="kpi-salary" />}
                </div>

                {/* The KPI pending cards and the KPI set-up panel used to sit
                    here. They were moved out: the dashboard is the HR overview,
                    and KPI chasing belongs with the KPI work. Everything they
                    showed is on the KPI Center, one click away in the sidebar.

                    What took their place is the thing HR is actually chasing on
                    a Monday morning: which seats are empty, and how long each
                    one has been. Named rather than counted — "4 open" tells you
                    nothing you can act on, "MUKUL's ENGINEER seat, 87 days"
                    tells you where to start. */}
                {openVacancies.length > 0 && (
                  <button data-testid="open-vacancies-card"
                    onClick={() => navigate('/requirement-tat')}
                    className="enter-up group relative isolate overflow-hidden w-full text-left rounded-[1.25rem] p-5 mb-6
                               transition-shadow duration-200 border border-[#FBCFE8]
                               shadow-[0_4px_20px_rgba(190,24,93,0.10)] hover:shadow-[0_10px_32px_rgba(190,24,93,0.18)]"
                    style={{ '--d': '40ms',
                             background: 'linear-gradient(135deg,#FFF1F6 0%,#FFE4EF 45%,#FBCFE8 100%)' }}>
                    {/* Gloss: one soft highlight across the top, and a faint
                        sheen at the foot so the pink reads as a glazed surface
                        rather than a flat fill. Pointer-events off and behind
                        the content, so neither can intercept the click. */}
                    <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-1/2 -z-10"
                          style={{ background: 'linear-gradient(180deg,rgba(255,255,255,0.72),rgba(255,255,255,0))' }} />
                    <span aria-hidden="true" className="pointer-events-none absolute -bottom-12 -right-10 w-56 h-56 rounded-full -z-10"
                          style={{ background: 'radial-gradient(circle,rgba(255,255,255,0.55),rgba(255,255,255,0) 70%)' }} />
                    <div className="flex items-center gap-4 mb-3">
                      <span className="grid place-items-center w-12 h-12 rounded-full flex-shrink-0 text-white bg-[#EC4899] shadow-[0_2px_8px_rgba(236,72,153,0.45)]"
                            aria-hidden="true">
                        <Briefcase className="w-[22px] h-[22px]" strokeWidth={2.1} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13.5px] text-[#374151]">Open vacancies</p>
                        <p className="mt-1">
                          <span className="text-[26px] font-extrabold tracking-tight text-[#BE185D]">
                            {openVacancies.length}
                          </span>
                          <span className="text-[13px] text-[#6B7280] ml-2">
                            {longestOpen != null
                              ? `position${openVacancies.length === 1 ? '' : 's'} to fill · longest waiting ${longestOpen} days`
                              : `position${openVacancies.length === 1 ? '' : 's'} to fill`}
                          </span>
                        </p>
                      </div>
                      <span aria-hidden="true"
                            className="grid place-items-center w-9 h-9 rounded-full flex-shrink-0 bg-white
                                       border border-[#F9A8D4] text-[#EC4899] transition-colors duration-150
                                       group-hover:bg-[#EC4899] group-hover:text-white">
                        <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
                      </span>
                    </div>
                    {/* The seats themselves. Longest-waiting first, because
                        that is the order they need attention in. */}
                    <div className="flex flex-wrap gap-2">
                      {openVacancies.slice(0, 8).map((v) => (
                        <span key={v.vacancy_id} data-testid={`open-vac-${v.vacancy_id}`}
                          className="text-xs bg-white/80 border border-[#F9A8D4] rounded-full px-3 py-1.5 text-[#831843]">
                          {v.designation || 'Position'}
                          <span className="text-[#BE5A8A]">
                            {v.department ? ` · ${v.department}` : ''}
                            {v.vacated_by ? ` · was ${v.vacated_by}` : ' · new position'}
                          </span>
                          {v.tat_days ? <span className="text-[#BE185D] font-semibold"> · {v.tat_days}d</span> : null}
                        </span>
                      ))}
                      {openVacancies.length > 8 && (
                        <span className="text-xs text-[#BE5A8A] self-center">
                          +{openVacancies.length - 8} more
                        </span>
                      )}
                    </div>
                  </button>
                )}

                {/* Staff and associates are the two halves of headcount, and
                    the same three questions are asked of each. One bubble
                    flips the charts below rather than duplicating them. */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="inline-flex rounded-full bg-white border border-[#E6E9F0] p-1"
                       data-testid="headcount-view-toggle">
                    {[['staff', 'Staff'], ['associates', 'Associates']].map(([k, label]) => (
                      <button key={k} onClick={() => setHeadcountView(k)}
                        data-testid={`headcount-view-${k}`}
                        aria-pressed={headcountView === k}
                        className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors
                          ${headcountView === k ? 'bg-[#2563EB] text-white' : 'text-[#5A6377] hover:bg-[#F1F5FD]'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-[#98A1B2]">
                    {showAssoc
                      ? (assocCharts
                          ? `${assocCharts.total} on the shop floor · ${inr(assocCharts.monthlyCost)}/month`
                          : 'Loading the associates register…')
                      : `${stats.total} staff on the roll`}
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                  <ChartCard title={showAssoc ? 'Associates by department' : 'Headcount by department'}
                             onReport={() => navigate(showAssoc ? '/associates' : '/employees')}
                             hint="Click a bar to open that group">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={(showAssoc ? assocCharts?.deptData : stats.deptData) || []}
                                layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F6" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#98A1B2' }} /><YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#5A6377' }} /><Tooltip />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer"
                             onClick={(d) => (showAssoc ? goAssocDept(d?.name) : goDept(d?.name))}>
                          {((showAssoc ? assocCharts?.deptData : stats.deptData) || []).map((_, i) => (
                            <Cell key={i} fill={ACCENTS[i % ACCENTS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* Associates have no level — the org ladder is a staff
                      structure. Who SUPPLIES them is the equivalent question,
                      and the one the contractor-wise cost report turns on. */}
                  <ChartCard title={showAssoc ? 'Associates by contractor' : 'Headcount by level'}
                             onReport={() => navigate(showAssoc ? '/associates' : '/employees')}
                             hint="Click a bar to open that group">
                    <ResponsiveContainer width="100%" height={260}>
                      {showAssoc ? (
                        <BarChart data={assocCharts?.contractorData || []} layout="vertical" margin={{ left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F6" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: '#98A1B2' }} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#5A6377' }} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={() => navigate('/associates')}>
                            {(assocCharts?.contractorData || []).map((_, i) => (
                              <Cell key={i} fill={ACCENTS[i % ACCENTS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      ) : (
                        <BarChart data={stats.levelData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F6" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5A6377' }} /><YAxis tick={{ fontSize: 11, fill: '#98A1B2' }} allowDecimals={false} /><Tooltip />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#6B8CB8" cursor="pointer" onClick={(d) => goLevel(d?.name)} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                <div className="grid grid-cols-1 mb-6">
                  <ChartCard title={showAssoc ? 'Associates by designation' : 'Headcount by designation'}
                             onReport={() => navigate(showAssoc ? '/associates' : '/employees')}
                             hint="Top 8 designations — click a bar to open that group">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={(showAssoc ? assocCharts?.desigData : stats.desigData) || []}
                                layout="vertical" margin={{ left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F6" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#98A1B2' }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: '#5A6377' }} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer"
                             onClick={(d) => (showAssoc ? goAssocDesig(d?.name) : goDesig(d?.name))}>
                          {((showAssoc ? assocCharts?.desigData : stats.desigData) || []).map((_, i) => (
                            <Cell key={i} fill={ACCENTS[i % ACCENTS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                {salary && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                    <ChartCard title="Salary draw by department (budget)" onReport={() => navigate('/salary-sheet')} hint="Click a bar to open that group">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={salary.deptSalary} layout="vertical" margin={{ left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F6" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: '#98A1B2' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} /><YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#5A6377' }} />
                          <Tooltip formatter={(v) => inr(v)} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => goDept(d?.name)}>
                            {salary.deptSalary.map((_, i) => <Cell key={i} fill={ACCENTS[i % ACCENTS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title="Share of total salary" onReport={() => navigate('/salary-sheet')} hint="Click a slice to open that department">
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie data={salary.deptSalary} dataKey="value" nameKey="name" cx="45%" cy="50%" outerRadius={95} cursor="pointer"
                            onClick={(d) => goDept(d?.name)} label={(d) => `${d.pct.toFixed(0)}%`} labelLine={false}>
                            {salary.deptSalary.map((_, i) => <Cell key={i} fill={ACCENTS[i % ACCENTS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v, n, p) => [`${inr(v)} (${p.payload.pct.toFixed(1)}%)`, p.payload.name]} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                  <ChartCard title="Joiners by year" onReport={() => navigate('/employees')} hint="Click a bar to open that group">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={stats.yearData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F6" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5A6377' }} /><YAxis tick={{ fontSize: 11, fill: '#98A1B2' }} allowDecimals={false} /><Tooltip />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#15A36E" cursor="pointer" onClick={(d) => goYear(d?.name)} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <ChartCard title="Newest joiners">
                      <ul className="space-y-2.5">{stats.recent.map((e) => (<li key={e.id} className="text-xs flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--g-success)' }} /><span className="min-w-0"><Link to={`/employees/${e.id}`} className="font-semibold text-[#172554] hover:text-brand-primary transition-colors">{e.name}</Link><span className="block text-[#94A3B8] truncate">{e.designation} · {e.doj}</span></span></li>))}</ul>
                    </ChartCard>
                    <ChartCard title="Longest serving">
                      <ul className="space-y-2.5">{stats.longest.map((e) => (<li key={e.id} className="text-xs flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--g-important)' }} /><span className="min-w-0"><Link to={`/employees/${e.id}`} className="font-semibold text-[#172554] hover:text-brand-primary transition-colors">{e.name}</Link><span className="block text-[#94A3B8] truncate">{e.designation} · {e.doj}</span></span></li>))}</ul>
                    </ChartCard>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
